import { User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuthStore } from '@/stores/authStore';
import { useLogout } from '@/lib/hooks/useLogout';
import { Avatar } from '@/features/Profile/Avatar';
import { cn } from '@/lib/utils';

/**
 * The account dropdown — name, email, Profile, Settings, Sign out.
 *
 * Extracted from StudentTopBar because the problem workspace needs it too, and
 * the workspace is deliberately rendered outside AppLayout (see __root.tsx),
 * so it inherits no navbar at all. Before this existed, Profile, Settings and
 * Sign out were simply unreachable from /problems/$slug.
 *
 * Four other top bars (AppNavbar, Trainer, Org, Admin) still inline their own
 * near-identical copy. They are left alone on purpose: converting them is a
 * wider blast radius than this change earns, and each has small deliberate
 * differences (no role line, different avatar size). This is the shared
 * definition for anything new.
 *
 * Safe outside a layout: the user comes from the Zustand auth store and the
 * dropdown renders through DropdownMenu.Portal, so it is never clipped by the
 * split-pane / editor ancestors that would trap an absolutely-positioned menu.
 */
export function UserMenu({
  /** Avatar diameter. The workspace bar is 48px tall and cannot take the 32px
   *  the 90px student bar uses without crowding its own controls. */
  size = 32,
  /** The name/role block is dead weight in a dense bar where the avatar alone
   *  reads as "your account". */
  showName = true,
  className,
}: {
  size?: number;
  showName?: boolean;
  className?: string;
}) {
  const { user } = useAuthStore();
  const username = user?.username ?? 'Student';
  const handleLogout = useLogout();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className={cn(
            'flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 outline-none transition-colors hover:bg-muted',
            className,
          )}
        >
          <Avatar src={user?.avatarUrl} name={username} size={size} />
          {showName && (
            <>
              <div className="hidden flex-col items-start sm:flex">
                <span className="text-[13px] font-semibold leading-tight text-foreground">{username}</span>
                <span className="text-[11px] capitalize leading-tight text-muted-foreground">
                  {user?.role ?? 'Student'}
                </span>
              </div>
              <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 w-[260px] rounded-xl border border-border bg-background shadow-xl"
          sideOffset={8}
          align="end"
        >
          <div className="flex items-center gap-3 p-4 pb-3">
            <Avatar src={user?.avatarUrl} name={username} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{username}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          <DropdownMenu.Separator className="h-px bg-border" />

          <div className="p-1.5">
            <DropdownMenu.Item
              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted outline-none"
              asChild
            >
              <Link to="/profile">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>Profile</span>
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted outline-none"
              asChild
            >
              <Link to="/settings">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <span>Settings</span>
              </Link>
            </DropdownMenu.Item>
          </div>

          <DropdownMenu.Separator className="h-px bg-border" />

          <div className="p-1.5">
            <DropdownMenu.Item
              onClick={handleLogout}
              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10 outline-none"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
